// Архив с документи: преглед/изтегляне; управляващият може да праща
// документ към наемателя. Наемателят вижда изпратените + автоматичните.

import React, { useCallback, useState } from 'react';
import { Linking, Pressable, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { api, API_URL } from '../api';
import { useAuth } from '../AuthContext';
import { showError, showSuccess } from '../toast';
import { Badge, Button, Card, COLORS, Muted, Screen, Title } from '../ui';
import { getLocale, t } from '../i18n';

export const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  CONTRACT: 'Договор',
  INVOICE: 'Фактура',
  RECEIPT: 'Бележка за плащане',
  DEPOSIT_RECEIPT: 'Разписка за депозит',
  NOTICE: 'Предизвестие',
  HANDOVER_PROTOCOL: 'Протокол',
  OTHER: 'Документ',
};

export default function DocumentsScreen() {
  const { user } = useAuth();
  const isManager = user?.role === 'LANDLORD' || user?.role === 'AGENCY';
  const [groups, setGroups] = useState<any[]>([]);
  // Бланката на договора се показва само при активен план (има имот)
  const [planActive, setPlanActive] = useState(false);
  const [openApartments, setOpenApartments] = useState<Record<string, boolean>>({});
  const total = groups.reduce(
    (n: number, g: any) => n + g.tenants.reduce((m: number, t: any) => m + t.documents.length, 0),
    0
  );

  const load = useCallback(() => {
    api('/documents')
      .then((r) => {
        setGroups(r.groups ?? []);
        // първият имот е отворен, останалите — свити
        setOpenApartments((prev) =>
          Object.keys(prev).length ? prev : Object.fromEntries((r.groups ?? []).map((g: any, i: number) => [g.apartmentId ?? 'x', i === 0]))
        );
      })
      .catch((e) => showError(e.message));
    // Бланката на договора е част от плана — крие се, докато няма имот
    if (isManager) {
      api('/billing/subscription')
        .then((r) => setPlanActive(r.access?.active && r.access?.reason !== 'NO_APARTMENTS_YET'))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useFocusEffect(load);

  const send = async (id: string) => {
    try {
      await api(`/documents/${id}/send`, { body: {} });
      showSuccess(t('Документът е изпратен към наемателя.'));
      load();
    } catch (e: any) {
      showError(e.message);
    }
  };

  // Празен договор по образеца — за попълване изцяло на ръка
  const blankContract = async () => {
    try {
      const res = await api('/documents/contract/blank', { body: {} });
      Linking.openURL(`${API_URL}${res.fileUrl}`);
    } catch (e: any) {
      showError(e.message);
    }
  };

  return (
    <Screen>
      <Title>{t('Документи')}</Title>
      {isManager && planActive && (
        <Card>
          <Muted>{t('Празна бланка на договора за наем — всички полета с многоточия, за попълване на ръка.')}</Muted>
          <Button title={t('Изтегли празен договор (PDF)')} variant="secondary" onPress={blankContract} />
        </Card>
      )}
      {total === 0 && (
        <Card>
          <Muted>
            {isManager
              ? t('Още няма документи. Генерирайте договор, фактура или разписка от екрана на имота.')
              : t('Още няма документи. Тук ще виждате изпратените от наемодателя + автоматичните бележки и протоколи.')}
          </Muted>
        </Card>
      )}

      {/* Групирано: имот → наемател → документи */}
      {groups.map((g) => {
        const key = g.apartmentId ?? 'no-apartment';
        const count = g.tenants.reduce((n: number, t: any) => n + t.documents.length, 0);
        const open = openApartments[key];
        return (
          <View key={key} style={{ marginBottom: 12 }}>
            <Pressable onPress={() => setOpenApartments((o) => ({ ...o, [key]: !o[key] }))}>
              <Card style={{ marginBottom: open ? 8 : 0, backgroundColor: COLORS.bg }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text style={{ flex: 1, fontSize: 16, fontWeight: '700', color: COLORS.text }}>
                    🏠 {g.apartmentLabel}
                  </Text>
                  <Badge text={String(count)} />
                  <Text style={{ color: COLORS.muted, marginLeft: 8 }}>{open ? '▾' : '▸'}</Text>
                </View>
              </Card>
            </Pressable>

            {open &&
              g.tenants.map((tn: any) => (
                <View key={tn.tenancyId ?? 'no-tenancy'} style={{ marginLeft: 10 }}>
                  <Text style={{ color: COLORS.muted, fontWeight: '600', marginBottom: 4, marginTop: 4 }}>
                    👤 {tn.tenantLabel} · {tn.documents.length}
                  </Text>
                  {tn.documents.map((d: any) => (
                    <Card key={d.id}>
                      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Badge text={t(DOCUMENT_TYPE_LABELS[d.type] ?? d.type)} />
                        <Muted>{new Date(d.createdAt).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG')}</Muted>
                      </View>
                      <Text style={{ fontSize: 16, fontWeight: '600', color: COLORS.text, marginTop: 6 }}>{d.title}</Text>
                      <Button
                        title={t('Отвори (PDF)')}
                        variant="secondary"
                        onPress={() => Linking.openURL(`${API_URL}${d.fileUrl}`)}
                      />
                      {isManager &&
                        (d.sentToTenantAt ? (
                          <Badge
                            text={t('Изпратен ✓ ({date})', { date: new Date(d.sentToTenantAt).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') })}
                            color={COLORS.success}
                          />
                        ) : (
                          <Button title={t('Изпрати на наемателя')} onPress={() => send(d.id)} />
                        ))}
                    </Card>
                  ))}
                </View>
              ))}
          </View>
        );
      })}
    </Screen>
  );
}
